import { createContext, useContext, useEffect, useState } from "react";
import { User } from "../types";
import { UserService } from "../services/user-service";
import { useAuth } from "./auth-context";

interface UserContextProps {
  user: User | null;
  isLoading: boolean;
  error: string | null;
  refetchUser: () => void;
  clearUser: () => void;
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

export function UserProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { token, isAuthenticated } = useAuth();

  const fetchUser = async () => {
    if (!isAuthenticated || !token) {
      setUser(null);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);
    
    const userService = new UserService();
    await userService.getUser(
      null,
      (data) => {
        setUser(data);
        setIsLoading(false);
      },
      (errorMessage) => {
        setError(errorMessage);
        setIsLoading(false);
      }
    );
  };

  const clearUser = () => {
    setUser(null);
    setError(null);
    setIsLoading(false);
  };

  useEffect(() => {
    if (isAuthenticated && token) {
      fetchUser();
    } else {
      clearUser();
    }
  }, [isAuthenticated, token]);

  return (
    <UserContext.Provider
      value={{
        user,
        isLoading,
        error,
        refetchUser: fetchUser,
        clearUser,
      }}
    >
      {children}
    </UserContext.Provider>
  );
}

export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error("useUser debe ser usado dentro de UserProvider");
  }
  return context;
};
